import { ElMessageBox, ElMessage } from 'element-plus'
import { t } from '@/plugins/i18n'
import store, { useRtfix, useSite } from '@/store'
import router from '@/router'
import { getCartAmount } from '@/apis/shopcart'
import { isShopUnbind, isAllShopUnbind } from '@/apis/user'
import { channelList } from '@/common/promotionChannel'

interface Options {
  msg: string;
  [key: string]: any;
}
interface F {
  (): void;
}

export const confirmEdit = (ok: () => void, cancel: () => void): void => {
  if (store.getters.editing) {
    ElMessageBox.confirm(t('当前页面的修改内容未保存，是否离开'), t('提示'), {
      confirmButtonText: t('确定'),
      cancelButtonText: t('取消'),
      type: 'warning'
    })
      .then(() => {
        store.commit('status/editing', false)
        ok()
      })
      .catch(cancel)
  } else {
    ok()
  }
}

export const normal = (
  { msg, ...options }: Options,
  ok: F,
  cancel: F
): void => {
  ElMessageBox.confirm(t(msg), t('提示'), {
    confirmButtonText: t('确认'),
    cancelButtonText: t('取消'),
    type: 'warning',
    ...options
  })
    .then(ok)
    .catch(cancel)
}

export const confirmUnlike = (ok: () => void, cancel: () => void): void => {
  normal({ msg: '确定不再显示该商品' }, ok, cancel)
}

export const confirmDeleteSku = (ok: () => void, cancel: () => void): void => {
  normal({ msg: '确定删除该商品' }, ok, cancel)
}

export const confirmClearCart = (ok: () => void, cancel: () => void): void => {
  normal({ msg: '确定清空购物车' }, ok, cancel)
}

export const confirmDelAdtCfg = (ok: () => void, cancel: () => void): void => {
  normal({ msg: '确定删除此配置' }, ok, cancel)
}

export const confirmLogoff = (ok: () => void, cancel: () => void): void => {
  normal({ msg: '确定注销此账号' }, ok, cancel)
}

export const confirmDelAddr = (ok: () => void, cancel: () => void): void => {
  normal({ msg: '确定删除此地址' }, ok, cancel)
}

export const continueOEM = (ok: () => void, cancel: () => void): void => {
  normal({ msg: '上次寻源未完成，是否继续' }, ok, cancel)
}

export const confirmDelTaxAccount = (
  ok: () => void,
  cancel: () => void
): void => {
  normal({ msg: '确定删除此扣税账号' }, ok, cancel)
}

export const valiAccess = (to: any, from: any, user: any) => {
  const levelDict: any = new Map()
  levelDict.set(0, {
    access: 0,
    msg: t(
      '我们的 OEM 仅适用于商务舱和头等舱成员。如果您想使用OEM，请从[会员信息]更改会员等级。如果您有任何问题，请联系我们的在线客服。'
    )
  })
  if (to.meta.memberLevel && to.path?.indexOf('/oem') > -1) {
    const level: any = user?.membership?.templateLevel ?? 0
    // 除了日本其他站点等级-1，会员
    const rule = levelDict.get(useSite() === 'ja' ? level : level - 1)
    if (rule?.access < to.meta.memberLevel) {
      normal(
        {
          msg: rule.msg,
          confirmButtonText: t('充值会员'),
          cancelButtonText: t('关闭')
        },
        () => {
          router.push(useRtfix('VipLevel'))
        },
        () => {}
      )
      return from
    }
  }
}

export const confirmLogin = <T>(
  fn: () => void | Promise<T>,
  fail?: () => void | Promise<T>
): false | void | Promise<T | void | false> => {
  const token = store.getters.token
  const curShop = store.getters.curShop
  if (token && curShop) {
    return fn()
  } else {
    if (!token) {
      return new Promise((resolve) => {
        fail && fail()
        const isReturn = channelNoLoginJump()
        if (isReturn) return
        store.commit('status/onLogin', () => {
          store.commit('status/onLogin', null)
          if (store.getters.curShop) {
            resolve(fn())
          } else {
            store.commit('status/toCreateShop', true)
            setTimeout(() => {
              ElMessage.error(t('当前账号下无店铺，请创建店铺后再执行操作'))
            }, 100)
            fail && fail()
            resolve(false)
          }
        })
      })
    } else {
      fail && fail()
      store.commit('status/toCreateShop', true)
      setTimeout(() => {
        ElMessage.error(t('当前账号下无店铺，请创建店铺后再执行操作'))
      }, 100)
      return false
    }
  }
}

// 同confirmLogin但是不判断店铺
export const confirmLoginWithNoCurShop = <T>(
  fn: () => void | Promise<T>,
  fail?: () => void | Promise<T>
): false | void | Promise<T | void | false> => {
  const token = store.getters.token
  if (token) {
    return fn()
  } else {
    return new Promise((resolve) => {
      fail && fail()
      const isReturn = channelNoLoginJump()
      if (isReturn) return
      store.commit('status/onLogin', () => {
        store.commit('status/onLogin', null)
        resolve(fn())
      })
    })
  }
}

export const confirmFinishAdd = async ({
  total,
  text = '留在此页',
  routeName = '',
  countryCode = 'CN',
  isAll
}: {
  total: number;
  text?: string;
  routeName?: string;
  countryCode: string;
  isAll?: boolean;
}): Promise<void> => {
  const amount = await getCartAmount().then((res) => res.data)
  ElMessageBox.confirm(
    `
          <div>${(isAll
            ? t('X件商品已添加至(一口价)购物车')
            : t('X件商品已添加至购物车')
          ).replace('X', total.toString())}</div>
          <div>${t('当前购物车共X种商品').replace('X', amount)}</div>
        `,
    t('添加成功'),
    {
      dangerouslyUseHTMLString: true,
      confirmButtonText: t('去结算'),
      cancelButtonText: t(text),
      type: 'success'
    }
  )
    .then(() => {
      router.replace(useRtfix('shopcart', { query: { sc: countryCode } }))
    })
    .catch(() => {
      routeName && router.replace(useRtfix(routeName))
    })
}

export const confirmApiUnbind = async (
  isAll = false,
  platform = 1
): Promise<boolean> => {
  const msg = store.getters.isSub
    ? t('很抱歉！您的店铺（X）因token失效需重新绑定，如需帮助请联系客服')
    : t('很抱歉！您的店铺（X）因token失效需重新绑定，如需帮助请联系客服')
  const api = isAll ? () => isAllShopUnbind() : () => isShopUnbind()
  return api().then((res) => {
    // const data = res.data?.customerShopName?res.data:
    const data = Array.isArray(res.data)
      ? res.data
      : res.data
        ? [res.data]
        : []
    if (data.length) {
      normal(
        {
          msg: msg.replace(
            'X',
            data.map((v: any) => v.customerShopName).join()
          ),
          confirmButtonText: t('去授权')
        },
        () => {
          router.push(
            useRtfix('AuthorizedStore', {
              query: { customerShopPlatform: platform }
            })
          )
        },
        () => {}
      )
      return true
    } else {
      return false
    }
  })
}
export const getParams = () => {
  const url = location.search
  const theRequest: any = {}
  if (url.indexOf('?') !== -1) {
    const str = url.substr(1)
    const strs = str.split('&')
    for (let i = 0; i < strs.length; i++) {
      theRequest[strs[i].split('=')[0]] = strs[i].split('=')[1]
    }
  }
  return theRequest
}

const channelNoLoginJump = () => {
  if (location.href.indexOf('/goods/') === -1) return false
  // 兼容渠道进入详情跳转注册页的逻辑
  const query = getParams()
  const { channel } = query
  const isPromotionChannelEnter = !!channelList[channel]
  if (isPromotionChannelEnter) {
    router.push({
      path: '/signup',
      query: {
        redirect: location.pathname + location.search,
        channel: channel,
        channelRedirect: 1
      }
    })
  } else {
    // 普通商品详情点击未登录进入登录页
    router.push({
      path: '/login',
      query: {
        redirect: location.pathname + location.search
      }
    })
  }
  return true
}
